import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ImageCarousel } from '../components/ui/ImageCarousel';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { VideosShowcase } from '../components/VideosShowcase';
import { ReviewsShowcase } from '../components/ReviewsShowcase';
import { ImageGalleryModal } from '../components/ui/ImageGalleryModal';
import { 
  Phone, MessageCircle, ArrowRight, Star, MapPin, Clock, Shield, 
  Wrench, BadgeCheck, Users, ChevronRight, ChevronLeft, Grid3x3, Building, Droplets,
  Images, ZoomIn
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const trustBadges = [
  { icon: MapPin, label: 'Local Expert', desc: 'Faridabad & Ballabhgarh' },
  { icon: BadgeCheck, label: 'Quality Materials', desc: 'Premium Grade' },
  { icon: Wrench, label: 'Custom Fabrication', desc: 'Made to Order' },
  { icon: Users, label: 'Residential + Commercial', desc: 'All Projects' },
];

const featuredServices = [
  { id: 'aluminium-partitions', name: 'Aluminium Partitions', icon: Grid3x3, color: 'sky' },
  { id: 'toughened-glass-partitions', name: 'Toughened Glass', icon: Building, color: 'orange' },
  { id: 'acp-front-elevation', name: 'ACP Front Elevation', icon: Building, color: 'sky' },
  { id: 'shower-enclosures', name: 'Shower Enclosures', icon: Droplets, color: 'orange' },
];

// Hero carousel images
const heroImages = [
  { url: 'https://images.unsplash.com/photo-1737216155207-6775db75f625?w=1920&q=80', alt: 'Modern Glass Building' },
  { url: 'https://images.unsplash.com/photo-1764296377290-51123bc0cbce?w=1920&q=80', alt: 'ACP Facade' },
  { url: 'https://images.unsplash.com/photo-1770992176317-382f51aff35d?w=1920&q=80', alt: 'Glass Partition' },
  { url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80', alt: 'Modern Interior' },
];

export const Home = () => {
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productSlide, setProductSlide] = useState(0);
  const [projectSlide, setProjectSlide] = useState(0);
  const [galleryModal, setGalleryModal] = useState({ open: false, item: null, type: '' });

  // Open gallery modal for any item type
  const openGallery = (item, type) => {
    const allImages = [item.image, ...(item.images || [])].filter(Boolean);
    setGalleryModal({ open: true, item: { ...item, allImages }, type });
  };

  const closeGallery = () => {
    setGalleryModal({ open: false, item: null, type: '' });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, productsRes, projectsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/products`),
          axios.get(`${API}/projects`),
          axios.get(`${API}/reviews`),
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setProducts(productsRes.data);
        setProjects(projectsRes.data);
        setReviews(reviewsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-slide products
  useEffect(() => {
    if (products.length <= 4) return;
    const timer = setInterval(() => {
      setProductSlide((prev) => (prev + 1) % (products.length - 3));
    }, 3000);
    return () => clearInterval(timer);
  }, [products.length]);

  // Auto-slide projects
  useEffect(() => {
    if (projects.length <= 3) return;
    const timer = setInterval(() => {
      setProjectSlide((prev) => (prev + 1) % (projects.length - 2));
    }, 4000);
    return () => clearInterval(timer);
  }, [projects.length]);

  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I am interested in your aluminium and glass services.')}`;

  const nextProductSlide = () => {
    setProductSlide((prev) => (prev + 1) % Math.max(1, products.length - 3));
  };

  const prevProductSlide = () => {
    setProductSlide((prev) => (prev - 1 + Math.max(1, products.length - 3)) % Math.max(1, products.length - 3));
  };

  const nextProjectSlide = () => {
    setProjectSlide((prev) => (prev + 1) % Math.max(1, projects.length - 2));
  };

  const prevProjectSlide = () => {
    setProjectSlide((prev) => (prev - 1 + Math.max(1, projects.length - 2)) % Math.max(1, projects.length - 2));
  };

  return (
    <div data-testid="home-page">
      <SEOHead {...SEO_CONFIG.home} />
      
      {/* Hero Section with Carousel */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Carousel */}
        <div className="absolute inset-0">
          <ImageCarousel 
            images={heroImages} 
            autoPlay={true} 
            interval={5000}
            showArrows={false}
            showDots={false}
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-32 md:py-40">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6 animate-fade-in-up">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span>5.0 Rated on Google</span>
              <span className="mx-2 w-1 h-1 bg-white/40 rounded-full"></span>
              <span>Trusted Local Business</span>
            </div>

            {/* Headline */}
            <h1 className="font-['Oswald'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up stagger-1">
              Premium Aluminium, Glass & 
              <span className="text-orange-400"> Front Elevation</span> Solutions in Faridabad
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up stagger-2">
              Specializing in all types of interior and exterior glass & aluminium works — 
              partitions, doors, windows, railings, ACP/HPL facades, and toughened glass installations 
              for homes, offices, and commercial spaces.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12 animate-fade-in-up stagger-3">
              <a href="tel:08512853955">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 h-14 text-base font-semibold" data-testid="hero-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 h-14 text-base font-semibold" data-testid="hero-quote-btn">
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#25d366] hover:bg-[#20bd5a] text-white px-8 h-14 text-base font-semibold" data-testid="hero-whatsapp-btn">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up stagger-4">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-3 px-4 py-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                  <badge.icon className="w-6 h-6 text-sky-400" />
                  <div>
                    <p className="text-white font-medium text-sm">{badge.label}</p>
                    <p className="text-slate-400 text-xs">{badge.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Quick Navigation Links */}
      <section className="py-8 bg-white border-b" data-testid="quick-nav-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            <Link 
              to="/services" 
              className="group flex flex-col items-center p-4 md:p-6 rounded-xl bg-sky-50 hover:bg-sky-100 transition-all hover:shadow-lg"
              data-testid="quick-nav-services"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-sky-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Wrench className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <span className="font-['Oswald'] font-semibold text-slate-900 text-sm md:text-lg">Our Services</span>
              <span className="text-slate-500 text-xs md:text-sm mt-1 hidden md:block">View All Services</span>
            </Link>

            <Link 
              to="/products" 
              className="group flex flex-col items-center p-4 md:p-6 rounded-xl bg-orange-50 hover:bg-orange-100 transition-all hover:shadow-lg"
              data-testid="quick-nav-products"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-500 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Building className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <span className="font-['Oswald'] font-semibold text-slate-900 text-sm md:text-lg">Products</span>
              <span className="text-slate-500 text-xs md:text-sm mt-1 hidden md:block">Browse Products</span>
            </Link>

            <Link 
              to="/gallery" 
              className="group flex flex-col items-center p-4 md:p-6 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all hover:shadow-lg"
              data-testid="quick-nav-gallery"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-700 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Grid3x3 className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <span className="font-['Oswald'] font-semibold text-slate-900 text-sm md:text-lg">Recent Works</span>
              <span className="text-slate-500 text-xs md:text-sm mt-1 hidden md:block">View Gallery</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 bg-slate-50" data-testid="featured-services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">OUR EXPERTISE</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What We Specialize In
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {featuredServices.map((service, index) => (
              <Link 
                key={service.id}
                to={`/services#${service.id}`}
                className="group"
              >
                <Card className="h-full border-0 shadow-md hover:shadow-xl transition-all hover:-translate-y-2">
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                      service.color === 'sky' ? 'bg-sky-100 text-sky-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      <service.icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-['Oswald'] font-semibold text-lg text-slate-900 group-hover:text-sky-600 transition-colors">
                      {service.name}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="view-all-services-btn">
                View All Services
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Products Slider */}
      <section className="py-20 bg-white" data-testid="products-slider-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-sky-600 font-semibold mb-2">OUR PRODUCTS</p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Quality Products at Best Prices
              </h2>
              <div className="section-divider"></div>
            </div>
            <div className="flex items-center gap-2 mt-4 md:mt-0">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={prevProductSlide}
                className="rounded-full"
                data-testid="products-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={nextProductSlide}
                className="rounded-full"
                data-testid="products-next"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Link to="/products" className="ml-4">
                <Button variant="link" className="text-sky-600 hover:text-sky-700 p-0">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Products Carousel */}
          <div className="overflow-hidden">
            <div 
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${productSlide * 280}px)` }}
            >
              {products.map((product) => {
                const imageCount = 1 + (product.images?.length || 0);
                return (
                  <Card key={product.id} className="product-card flex-shrink-0 w-[260px] overflow-hidden hover-scale" data-testid={`product-${product.id}`}>
                    <div 
                      className="aspect-square overflow-hidden relative cursor-pointer group"
                      onClick={() => openGallery(product, 'product')}
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      {/* Image count badge */}
                      {imageCount > 1 && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <Images className="w-3 h-3" />
                            {imageCount}
                          </span>
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-slate-800" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <span className="text-xs text-sky-600 font-medium">{product.category}</span>
                      <h3 
                        className="font-semibold text-slate-900 mt-1 line-clamp-1 cursor-pointer hover:text-sky-600"
                        onClick={() => openGallery(product, 'product')}
                      >
                        {product.name}
                      </h3>
                      <p className="text-slate-500 text-xs mt-1 line-clamp-2">{product.description}</p>
                      <p className="product-price text-2xl font-bold mt-2">
                        ₹{product.price}<span className="text-sm font-normal text-slate-500">/{product.price_unit}</span>
                      </p>
                      <Link to="/contact" state={{ product: product.name }}>
                        <Button size="sm" className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white">
                          Enquire Now
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Slider Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.max(1, products.length - 3) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setProductSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === productSlide ? 'bg-sky-500 w-8' : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Projects Slider */}
      <section className="py-20 bg-slate-900" data-testid="projects-slider-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-orange-400 font-semibold mb-2">OUR WORK</p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-4">
                Recent Projects
              </h2>
              <div className="section-divider"></div>
            </div>
            <div className="flex items-center gap-2 mt-4 md:mt-0">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={prevProjectSlide}
                className="rounded-full border-white/30 text-white hover:bg-white/10"
                data-testid="projects-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={nextProjectSlide}
                className="rounded-full border-white/30 text-white hover:bg-white/10"
                data-testid="projects-next"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
              <Link to="/gallery" className="ml-4">
                <Button variant="link" className="text-orange-400 hover:text-orange-300 p-0">
                  View Gallery <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Projects Carousel */}
          <div className="overflow-hidden">
            <div 
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${projectSlide * 420}px)` }}
            >
              {projects.map((project) => {
                const imageCount = [project.image, ...(project.images || [])].filter(Boolean).length;
                return (
                  <div 
                    key={project.id} 
                    className="flex-shrink-0 w-[400px] aspect-[4/3] rounded-xl overflow-hidden relative group cursor-pointer"
                    data-testid={`project-${project.id}`}
                    onClick={() => openGallery(project, 'project')}
                  >
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                      loading="lazy" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>
                    {/* Image count badge */}
                    {imageCount > 1 && (
                      <div className="absolute top-4 left-4">
                        <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                          <Images className="w-3 h-3" />
                          {imageCount}
                        </span>
                      </div>
                    )}
                    {/* Zoom icon on hover */}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                        <ZoomIn className="w-5 h-5 text-slate-700" />
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full mb-2 inline-block">
                        {project.category}
                      </span>
                      {project.featured && (
                        <span className="px-3 py-1 bg-sky-500 text-white text-xs rounded-full mb-2 ml-2 inline-block">
                          Featured
                        </span>
                      )}
                      <h3 className="font-['Oswald'] text-xl font-semibold text-white">{project.title}</h3>
                      <p className="text-slate-300 text-sm mt-1 line-clamp-2">{project.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Slider Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.max(1, projects.length - 2) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setProjectSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === projectSlide ? 'bg-orange-500 w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Videos/Reels Showcase */}
      <VideosShowcase maxVideos={6} data-testid="videos-showcase-section" />

      {/* Client Reviews Showcase */}
      <ReviewsShowcase 
        maxReviews={6} 
        showVideoTestimonials={true}
        data-testid="reviews-showcase-section" 
      />

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <p className="text-sky-600 font-semibold mb-2">OUR SERVICES</p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Complete Glass & Aluminium Solutions
              </h2>
              <div className="section-divider"></div>
            </div>
            <Link to="/services" className="mt-4 md:mt-0">
              <Button variant="link" className="text-sky-600 hover:text-sky-700 p-0">
                See All Services <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const imageCount = 1 + (service.images?.length || 0);
              return (
                <Card key={service.id} className="service-card overflow-hidden group" data-testid={`service-card-${service.id}`}>
                  <div 
                    className="service-card-image relative cursor-pointer"
                    onClick={() => openGallery(service, 'service')}
                  >
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    {/* Image count badge */}
                    {imageCount > 1 && (
                      <div className="absolute top-4 right-4">
                        <span className="px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full flex items-center gap-1">
                          <Images className="w-3 h-3" />
                          {imageCount}
                        </span>
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-3">
                        <ZoomIn className="w-6 h-6 text-slate-800" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 
                      className="font-['Oswald'] font-semibold text-xl text-slate-900 mb-2 group-hover:text-sky-600 transition-colors cursor-pointer"
                      onClick={() => openGallery(service, 'service')}
                    >
                      {service.name}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {service.benefits?.slice(0, 2).map((benefit, i) => (
                        <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                          {benefit}
                        </span>
                      ))}
                    </div>
                    <Link to="/contact" state={{ service: service.name }}>
                      <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                        Get Quote
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews Preview */}
      <section className="py-20 bg-slate-50" data-testid="reviews-preview-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">CUSTOMER REVIEWS</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-slate-900">5.0</span>
              <span className="text-slate-500">on Google</span>
            </div>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {reviews.map((review) => (
              <Card key={review.id} className="testimonial-card" data-testid={`review-${review.id}`}>
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{review.review}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center font-semibold text-slate-600">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{review.name}</p>
                      <p className="text-xs text-slate-500">{review.source} Review</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/reviews">
              <Button variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="view-all-reviews-btn">
                See All Reviews
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-sky-700" data-testid="contact-strip-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to Transform Your Space?
              </h2>
              <p className="text-sky-100">Get a free consultation and quote for your aluminium & glass project</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:08512853955">
                <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 font-semibold" data-testid="strip-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  085128 53955
                </Button>
              </a>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#25d366] hover:bg-[#20bd5a] text-white font-semibold" data-testid="strip-whatsapp-btn">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold" data-testid="strip-quote-btn">
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <p className="text-sky-600 font-semibold mb-2">VISIT US</p>
              <h2 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Our Location
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center text-sky-600">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Address</p>
                    <p className="text-slate-600 text-sm">Chungi no:- 5, Mohana Rd, near Bikaner sweets, Uncha Gaon, Ballabhgarh, Faridabad, Haryana 121004</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Business Hours</p>
                    <p className="text-slate-600 text-sm">Monday - Saturday: 8 AM - 8 PM</p>
                    <p className="text-slate-500 text-sm">Sunday: Closed</p>
                  </div>
                </div>
                <a 
                  href="https://www.google.com/maps/search/Raj+Aluminum+Glass+Hardware+Ballabhgarh+Faridabad"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white" data-testid="get-directions-btn">
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:col-span-2 map-container h-[300px] lg:h-auto">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.5!2d77.32!3d28.35!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDIxJzAwLjAiTiA3N8KwMTknMTIuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '300px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Raj Aluminum Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={galleryModal.open}
        onClose={closeGallery}
        images={galleryModal.item?.allImages || []}
        title={galleryModal.item?.name || galleryModal.item?.title || ''}
      />
    </div>
  );
};

export default Home;
